import {useState} from 'react';

import {cn} from '../../lib/utils';
import {Button} from '../Button';
import {Divider} from '../Divider';
import {Icon} from '../Icon/Icon';
import {PopoverButton} from '../Popover';
import {Popover, PopoverContent, PopoverTrigger} from '../shadcn/popover';
import {Typography} from '../Typography';

import type {IconName} from '../../icons/icon-map';
import type {FC} from 'react';

export interface MoreMenuItem {
  icon: IconName;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  separator?: boolean;
  /** A small caption opening a group of items, shown above this item (after its separator). */
  heading?: string;
}

export interface MoreMenuProps {
  items: MoreMenuItem[];
  ariaLabel?: string;
  orientation?: 'horizontal' | 'vertical';
  variant?: 'ghost' | 'bordered';
}

const triggerStylesByVariant = {
  ghost: {
    buttonVariant: 'tertiary',
    buttonSize: 'icon-xs',
    buttonClassName: 'text-fg-secondary',
    iconClassName: 'size-4'
  },
  bordered: {
    buttonVariant: 'secondary',
    buttonSize: 'icon',
    buttonClassName: undefined,
    iconClassName: undefined
  }
} as const;

export const MoreMenu: FC<MoreMenuProps> = ({
  items,
  ariaLabel,
  orientation = 'horizontal',
  variant = 'ghost'
}) => {
  const {buttonVariant, buttonSize, buttonClassName, iconClassName} =
    triggerStylesByVariant[variant];
  const [isOpen, setIsOpen] = useState(false);

  if (items.length === 0) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <Button
            variant={buttonVariant}
            size={buttonSize}
            className={buttonClassName}
            aria-label={ariaLabel}
            icon={
              <Icon
                name={orientation === 'vertical' ? 'dots-vertical' : 'dots-horizontal'}
                className={iconClassName}
                aria-hidden="true"
              />
            }
          />
        }
      />
      <MoreMenuContent items={items} onSelect={() => setIsOpen(false)} />
    </Popover>
  );
};

export type MoreMenuSize = 'default' | 'lg';

export interface MoreMenuContentProps {
  items: MoreMenuItem[];
  /** Called before an item's own handler, e.g. to close the surrounding popover. */
  onSelect?: () => void;
  size?: MoreMenuSize;
}

const menuStylesBySize = {
  default: {
    sideOffset: 4,
    contentClassName: 'w-auto min-w-40 gap-1 p-1.25',
    separatorInsetClassName: undefined,
    separatorVariant: 'default',
    itemClassName:
      'gap-2 py-1.5 [&>span]:font-normal [&>span]:whitespace-nowrap *:data-[slot=icon]:size-4 *:data-[slot=icon]:text-fg-secondary'
  },
  lg: {
    sideOffset: 8,
    contentClassName: 'w-65 gap-0.5 rounded-lg p-1.5 shadow-md',
    separatorInsetClassName: 'px-1.5 py-1',
    separatorVariant: 'subtle',
    itemClassName:
      'min-h-10 gap-2.5 rounded-md px-3 py-0 hover:bg-background-secondary [&>span]:font-normal [&>span]:whitespace-nowrap [&>span]:text-fg *:data-[slot=icon]:size-4.5'
  }
} as const;

/** The menu's popup, for popovers that need their own trigger (e.g. a `SplitButton`). */
export const MoreMenuContent: FC<MoreMenuContentProps> = ({items, onSelect, size = 'default'}) => {
  const {sideOffset, contentClassName, separatorInsetClassName, separatorVariant, itemClassName} =
    menuStylesBySize[size];

  return (
    <PopoverContent align="end" sideOffset={sideOffset} className={contentClassName}>
      {items.map((item, index) => (
        <div key={index}>
          {item.separator && index > 0 && (
            <div className={separatorInsetClassName}>
              <Divider variant={separatorVariant} />
            </div>
          )}
          {item.heading && (
            <Typography size="xs" weight="semibold" color="tertiary" className="px-3 pt-1 pb-0.5">
              {item.heading}
            </Typography>
          )}
          <PopoverButton
            icon={item.icon}
            className={cn(
              itemClassName,
              item.destructive && '[&>span]:text-destructive *:data-[slot=icon]:text-destructive'
            )}
            onClick={() => {
              onSelect?.();
              item.onClick();
            }}
          >
            {item.label}
          </PopoverButton>
        </div>
      ))}
    </PopoverContent>
  );
};
